import type { Muscle } from '../catalog/muscles.js';
import {
  classifyChange,
  CHANGE_THRESHOLDS,
  type ChangeClassification,
} from '../decisions/change-policy.js';
import { clampWeeklyVolume } from '../safety/limits.js';
import { clamp } from '../units/round.js';
import { classifyVolume, type VolumeBand } from './volume.js';

/**
 * Ajuste de volumen por respuesta observada (docs/TRAINING_ENGINE.md §4.3).
 *
 * Es lo que convierte las bandas de §4.2 en algo vivo: dejan de ser un valor de
 * partida y se recalibran con lo que el atleta hace y con como se recupera.
 *
 * Puro y sin estado: recibe las señales ya calculadas y devuelve una propuesta.
 * Quien llama decide de donde salen los datos y si la propuesta se aplica.
 */

// ---------------------------------------------------------------------------
// Señales
// ---------------------------------------------------------------------------

/**
 * `unknown` existe a proposito y no es lo mismo que `flat`.
 *
 * Sin historial suficiente no se sabe si el rendimiento esta estable; leerlo
 * como estable haria que la regla de "estable + buena recuperacion" añadiera
 * series basandose en nada. Falta de dato no es dato.
 */
export type PerformanceTrend = 'rising' | 'flat' | 'falling' | 'unknown';

export type RecoveryState = 'good' | 'fair' | 'poor';

/**
 * Cambio semanal medio a partir del cual se considera que hay tendencia.
 *
 * CONVENCION declarada, no hallazgo: el e1RM estimado tiene ruido de varios
 * puntos porcentuales entre semanas. Un umbral mas bajo convertiria el ruido en
 * decisiones de volumen.
 */
export const E1RM_TREND_THRESHOLD = 0.015;

/**
 * Tendencia de rendimiento a partir del e1RM semanal, del mas antiguo al mas
 * reciente. Usa el cambio medio por semana en toda la ventana en lugar de
 * comparar solo las dos ultimas: una semana mala aislada no invierte la
 * tendencia de un bloque entero.
 */
export function performanceTrend(
  weeklyE1rmKg: readonly number[],
  threshold = E1RM_TREND_THRESHOLD,
): PerformanceTrend {
  const puntos = weeklyE1rmKg.filter((valor) => Number.isFinite(valor) && valor > 0);
  if (puntos.length < 2) return 'unknown';

  const primero = puntos[0] as number;
  const ultimo = puntos[puntos.length - 1] as number;
  const cambioMedio = (ultimo - primero) / primero / (puntos.length - 1);

  if (cambioMedio > threshold) return 'rising';
  if (cambioMedio < -threshold) return 'falling';
  return 'flat';
}

/** Umbrales de recuperacion. CONVENCION declarada; agujetas en escala 1-5. */
export const RECOVERY_THRESHOLDS = {
  readinessGood: 65,
  readinessPoor: 45,
  sorenessGood: 2.5,
  sorenessHigh: 3.5,
  intraSessionDropGood: 0.1,
  intraSessionDropPoor: 0.2,
} as const;

export interface RecoveryInput {
  /** Agujetas medias de la region, escala 1-5. 0 = ninguna declarada. */
  sorenessAvg: number;
  /** Caida de rendimiento dentro de la sesion, en tanto por uno. */
  intraSessionDropPct: number;
  /** Readiness medio de la semana, 0-100. */
  readinessScore: number;
}

/**
 * Combina las tres señales de recuperacion de §4.3.
 *
 * Basta con que UNA este mal para que la recuperacion no sea buena: son
 * indicadores de un mismo estado, y promediarlos dejaria que un buen descanso
 * tapara unas agujetas severas.
 */
export function recoveryState(input: RecoveryInput): RecoveryState {
  const mala =
    input.readinessScore <= RECOVERY_THRESHOLDS.readinessPoor ||
    input.sorenessAvg >= RECOVERY_THRESHOLDS.sorenessHigh ||
    input.intraSessionDropPct >= RECOVERY_THRESHOLDS.intraSessionDropPoor;
  if (mala) return 'poor';

  const buena =
    input.readinessScore >= RECOVERY_THRESHOLDS.readinessGood &&
    input.sorenessAvg <= RECOVERY_THRESHOLDS.sorenessGood &&
    input.intraSessionDropPct <= RECOVERY_THRESHOLDS.intraSessionDropGood;
  return buena ? 'good' : 'fair';
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

/** Paso de subida y recortes de §4.3, en series efectivas por semana. */
export const VOLUME_STEP_SETS = 2;
export const VOLUME_CUT_MODERATE_SETS = 2;
export const VOLUME_CUT_HARD_SETS = 4;

/** Semanas consecutivas que exigen las reglas 2 y 3. */
export const WEEKS_FOR_TREND_RULES = 2;

export interface VolumeAdjustmentInput {
  muscle: Muscle;
  /** Series efectivas de la semana que termina. */
  currentSets: number;
  /** Series con las que empezo el bloque: la referencia del limite duro. */
  blockStartSets: number;
  band: VolumeBand;
  performance: PerformanceTrend;
  /** Semanas consecutivas con esa misma tendencia. */
  weeksInTrend: number;
  recovery: RecoveryState;
  /** Agujetas medias de la region, escala 1-5. */
  sorenessAvg: number;
}

export interface VolumeAdjustment {
  muscle: Muscle;
  currentSets: number;
  /** Volumen propuesto para la semana siguiente, ya recortado por seguridad. */
  proposedSets: number;
  deltaSets: number;
  reasonCodes: string[];
  /** Menor o relevante, segun la politica central. Relevante pide aprobacion. */
  change: ChangeClassification;
  /** El limite duro tuvo que intervenir sobre la propuesta bruta. */
  clampedBySafety: boolean;
}

/**
 * Aplica las reglas de §4.3 a un musculo y devuelve la propuesta para la
 * semana siguiente.
 *
 * ORDEN DE COMPOSICION, que no es indiferente: la clasificacion se hace sobre
 * la propuesta BRUTA y el recorte de seguridad se aplica despues. Hacerlo al
 * reves desactivaria la regla 4 sin que se notara: `clampWeeklyVolume` recorta
 * al 20 % del inicio del bloque y `classifyChange` considera relevante lo que
 * PASA del 20 %, asi que clasificar lo ya recortado daria siempre "menor" y
 * ninguna propuesta pediria aprobacion jamas. El limite duro dice cuanto se
 * puede mover; la clasificacion dice si hace falta permiso para moverlo.
 */
export function adjustVolume(input: VolumeAdjustmentInput): VolumeAdjustment {
  const { band, currentSets } = input;
  const reasonCodes: string[] = [];
  let bruto = currentSets;
  let algunaReglaAplico = false;

  const agujetasAltas = input.sorenessAvg >= RECOVERY_THRESHOLDS.sorenessHigh;
  const sostenida = input.weeksInTrend >= WEEKS_FOR_TREND_RULES;

  if (input.performance === 'unknown') {
    // Sin historial no se toca nada. Es la unica respuesta honesta.
    reasonCodes.push('insufficient_history');
  } else if (input.performance === 'falling' && sostenida && agujetasAltas) {
    // Regla 3. El recorte mayor se reserva a cuando ademas la recuperacion es
    // mala; con agujetas altas pero recuperacion aceptable basta el menor.
    const recorte = input.recovery === 'poor' ? VOLUME_CUT_HARD_SETS : VOLUME_CUT_MODERATE_SETS;
    bruto = currentSets - recorte;
    algunaReglaAplico = true;
    reasonCodes.push('performance_falling_with_soreness');
  } else if (
    input.performance === 'rising' &&
    input.recovery === 'good' &&
    currentSets < band.targetMax
  ) {
    // Regla 1: sube y recupera bien, todavia por debajo del objetivo.
    bruto = currentSets + VOLUME_STEP_SETS;
    algunaReglaAplico = true;
    reasonCodes.push('performance_rising_recovery_good');
  } else if (
    input.performance === 'flat' &&
    input.recovery === 'good' &&
    sostenida &&
    currentSets < band.max
  ) {
    // Regla 2: estancado pero recuperando bien, se sube hasta el techo.
    bruto = currentSets + VOLUME_STEP_SETS;
    algunaReglaAplico = true;
    reasonCodes.push('performance_flat_room_to_grow');
  } else {
    reasonCodes.push('no_rule_applies');
  }

  // Sin regla que aplicar no hay propuesta, y punto.
  //
  // Esto NO es una comprobacion defensiva: sin ella, el recorte a la banda que
  // viene despues convierte "no aplica ninguna regla" en "sube al minimo de la
  // banda". Un musculo en 5 series con banda que empieza en 10 generaba una
  // propuesta de subir sin que ninguna señal lo respaldara, y la pantalla se
  // llenaba de tarjetas cuyo motivo era, literalmente, que no habia motivo.
  //
  // Que un musculo este por debajo de su minimo es informacion —la da
  // `volumeReport`— pero no una decision: §4.3 tiene cuatro reglas y ninguna
  // es "estas bajo el minimo".
  if (!algunaReglaAplico) {
    return {
      muscle: input.muscle,
      currentSets,
      proposedSets: currentSets,
      deltaSets: 0,
      reasonCodes,
      change: classifyChange({ kind: 'weekly_volume', magnitude: 0 }),
      clampedBySafety: false,
    };
  }

  // La banda acota lo que propone una regla: ninguna puede sacar al musculo de
  // su rango util.
  bruto = clamp(bruto, band.min, band.max);

  // Clasificacion sobre el bruto (ver nota de orden, arriba).
  const magnitud =
    input.blockStartSets > 0 ? Math.abs(bruto - input.blockStartSets) / input.blockStartSets : 0;
  const change = classifyChange({ kind: 'weekly_volume', magnitude: magnitud });

  const recortado = clampWeeklyVolume(bruto, input.blockStartSets);
  if (recortado.clamped) reasonCodes.push('clamped_by_safety_limit');

  return {
    muscle: input.muscle,
    currentSets,
    proposedSets: recortado.value,
    deltaSets: recortado.value - currentSets,
    reasonCodes,
    change,
    clampedBySafety: recortado.clamped,
  };
}

/**
 * Texto corto para la tarjeta de propuesta. La app no debe componer estas
 * frases: si el motivo vive junto a la regla, no pueden desincronizarse.
 */
export function explainAdjustment(adjustment: VolumeAdjustment, band: VolumeBand): string {
  const situacion = classifyVolume(adjustment.currentSets, band);
  const partes: string[] = [];

  if (adjustment.deltaSets > 0) partes.push(`Subir ${adjustment.deltaSets} series`);
  else if (adjustment.deltaSets < 0) partes.push(`Bajar ${Math.abs(adjustment.deltaSets)} series`);
  else partes.push('Mantener el volumen');

  partes.push(`(${adjustment.currentSets} → ${adjustment.proposedSets}, banda ${band.min}-${band.max})`);

  if (adjustment.reasonCodes.includes('insufficient_history')) {
    partes.push('· aún no hay historial suficiente');
  }
  if (situacion === 'above_max') partes.push('· por encima del máximo');
  if (situacion === 'below_min') partes.push('· por debajo del mínimo');
  if (adjustment.clampedBySafety) partes.push('· recortado por el límite de seguridad');
  if (adjustment.change.requiresApproval) {
    partes.push(`· supera el ${Math.round(CHANGE_THRESHOLDS.weeklyVolumePct * 100)} % del inicio del bloque: necesita tu aprobación`);
  }

  return partes.join(' ');
}
